// getCMSSummary > `/patients/${patientId}/encounters/${encounterId}/monthlySummary


import { z } from 'zod'

import http from '../../lib/http'
import { notify } from '../../lib/Notification/actions.js'
import { compartmentReceive } from '../PatientFolder/patientActions.js'

import { init } from './EncountersHistory/actions.js'

export const ENCOUNTER_SEARCH = 'ENCOUNTER_SEARCH'
export const ENCOUNTER_FOUND = 'ENCOUNTER_FOUND'
export const ENCOUNTER_FOUND_ERROR = 'ENCOUNTER_FOUND_ERROR'

export const ENCOUNTER_START = 'ENCOUNTER_START'
export const ENCOUNTER_START_ERROR = 'ENCOUNTER_START_ERROR'
export const ENCOUNTER_STARTED = 'ENCOUNTER_STARTED'

export const ENCOUNTER_SIGN = 'ENCOUNTER_SIGN'
export const ENCOUNTER_SIGNED = 'ENCOUNTER_SIGNED'
export const ENCOUNTER_SIGN_ERROR = 'ENCOUNTER_SIGN_ERROR'

export const ENCOUNTER_RESET = 'ENCOUNTER_RESET'

export const ENCOUNTER_GET_CHANGES = 'ENCOUNTER_GET_CHANGES'
export const ENCOUNTER_GET_CHANGES_SUCCESS = 'ENCOUNTER_GET_CHANGES_SUCCESS'

export const ENCOUNTER_GET = 'ENCOUNTER_GET'
export const ENCOUNTER_GOT = 'ENCOUNTER_GOT'

export const ENCOUNTER_DURATION = 'ENCOUNTER_DURATION'
export const ENCOUNTER_DURATION_ERROR = 'ENCOUNTER_DURATION_ERROR'

export const GRAND_ROUNDS_GET_SUMMARY = 'GRAND_ROUNDS_GET_SUMMARY'
export const GRAND_ROUNDS_GET_SUMMARY_SUCCESS =
  'GRAND_ROUNDS_GET_SUMMARY_SUCCESS'

export const ENCOUNTER_START_TIMER = 'ENCOUNTER_START_TIMER'
export const ENCOUNTER_STOP_TIMER = 'ENCOUNTER_STOP_TIMER'

export const CONTINUE_ENCOUNTER_CHECK = 'CONTINUE_ENCOUNTER_CHECK'

const commonEncounterSignFieldsSchema = z.object({
  urgency: z.string(),
  agentDoNotBill: z.boolean().nullish(),

  narrative: z.string(),

  user: z.string()
    .optional(),

  justification: z.string()
    .optional()
})

const dateOfServiceOverrideSchema = z.union([
  z.object({
    dateOfServiceOverridden: z.literal(true),

    dateOfServiceOverrideDate: z.string()
  }),

  z.object({
    dateOfServiceOverridden: z.literal(false)
      .nullable()
      .optional()
  })
])

const timeSchema = z.union([
  z.object({
    time: z.enum([
      'computed',
      'non-billable'
    ])
  }),

  z.object({
    time: z.literal('override'),

    timeHours: z.string(),

    timeMinutes: z.string(),

    timeDetails: z.string()
  })
    .and(dateOfServiceOverrideSchema)
])

const medicalDirectorEscalationSchema = z.union([
  z.object({
    escalateToMedicalDirector: z.literal(true),
    escalateToMedicalDirectorComments: z.string().optional().nullable(),
    medicalDirector: z.string()
  }),

  z.object({
    escalateToMedicalDirector: z.literal(false)
      .nullable()
      .optional()
  })
])

const supervisorEscalationSchema = z.union([
  z.object({
    escalateToSupervisor: z.literal(true),
    escalateToSupervisorComments: z.string().optional().nullable(),
    supervisor: z.string()
  }),

  z.object({
    escalateToSupervisor: z.literal(false)
      .nullable()
      .optional()
  })
])

const dispositionSchema = z.object({
  participant: z.string(),

  outcome: z.string(),

  reason: z.string()
})

const administrativeEncounterFormSchema = z.object({
  type: z.literal('administrative')
})

const grandRoundsEncounterFormSchema = z.object({
  type: z.literal('grandRounds')
})

const homeEncounterFormSchema = z.object({
  type: z.literal('home')
})
  .merge(dispositionSchema)

const phoneEncounterFormSchema = z.object({
  type: z.literal('phone'),

  origin: z.string()
})
  .merge(dispositionSchema)

const mobileEncounterFormSchema = z.object({
  type: z.literal('mobile'),

  origin: z.string()
})
  .merge(dispositionSchema)

const emailEncounterFormSchema = z.object({
  type: z.literal('email'),

  origin: z.string()
})
  .merge(dispositionSchema)

const cmsEncounterFormSchema = z.object({
  type: z.literal('cms')
})

const apsSchema = z.object({
  type: z.literal('APS'),
  reportType: z.string(),
  presentMedicalStatus: z.string(),
  careCosts: z.string(),
  recommendations: z.string()
})

const researchReviewSchema = z.object({
  type: z.literal('Research'),
  reportType: z.string(),
  responseFindings: z.string(),
  costSavings: z.string(),
  questionRequest: z.string()
})

export const encounterBodySchema = z.discriminatedUnion('type', [
  administrativeEncounterFormSchema,
  grandRoundsEncounterFormSchema,
  homeEncounterFormSchema,
  phoneEncounterFormSchema,
  mobileEncounterFormSchema,
  emailEncounterFormSchema,
  cmsEncounterFormSchema,
  apsSchema,
  researchReviewSchema
])
  .and(timeSchema)
  .and(commonEncounterSignFieldsSchema)
  .and(medicalDirectorEscalationSchema)
  .and(supervisorEscalationSchema)

export function getCMSSummary (patientId, encounterId) {
  return dispatch => {
    dispatch({ type: ENCOUNTER_GET_CHANGES })
    return http
      .getRequest(
        `/patients/${patientId}/encounters/${encounterId}/monthlySummary`
      )
      .then(response => {
        dispatch({
          type: ENCOUNTER_GET_CHANGES_SUCCESS,
          reportData: response.data,
          reportHtml: response.html
        })
      })
  }
}

export function getAPSReport (patientId, encounterId) {
  return dispatch => {
    dispatch({ type: ENCOUNTER_GET_CHANGES })
    return http
      .getRequest(
        `/patients/${patientId}/encounters/${encounterId}/apsReport`
      )
      .then(response => {
        dispatch({
          type: ENCOUNTER_GET_CHANGES_SUCCESS,
          reportData: response.data,
          reportHtml: response.html
        })
      })
  }
}

export function getResearchReviewReport (patientId, encounterId) {
  return dispatch => {
    dispatch({ type: ENCOUNTER_GET_CHANGES })
    return http
      .getRequest(
        `/patients/${patientId}/encounters/${encounterId}/researchReviewReport`
      )
      .then(response => {
        dispatch({
          type: ENCOUNTER_GET_CHANGES_SUCCESS,
          reportData: response.data,
          reportHtml: response.html
        })
      })
  }
}

export function getGrandRoundsSummary (patientId, encounterId) {
  return dispatch => {
    dispatch({ type: GRAND_ROUNDS_GET_SUMMARY })

    return http
      .getRequest(
        `/patients/${patientId}/encounters/${encounterId}/grandRounds`
      )
      .then(response => {
        dispatch({
          type: GRAND_ROUNDS_GET_SUMMARY_SUCCESS,
          reportData: response.data
        })
      })
  }
}

export function encounterSearch (patientId, user, clientName) {
  const userId = localStorage.getItem('asUserId') ? localStorage.getItem('asUserId') : user.sub

  return dispatch => {
    dispatch({
      type: ENCOUNTER_SEARCH,
      patientId,
      userId
    })

    return http
      .getRequest(
        `/patients/${patientId}/unfinished-encounter?author_id=${userId}`,
        {},
        { clientName }
      )
      .then(response => {
        dispatch({
          type: ENCOUNTER_FOUND,
          encounter: response?.currentUserEncounter,
          otherEncounters: response?.otherUsersEncounter
        })
      })
      .catch(error => {
        dispatch({
          type: ENCOUNTER_FOUND_ERROR,
          error
        })
        dispatch(notify(`Encounter Error ${error}`))
      })
  }
}

export function continueEncounterCheck (patientId, user, asUserId, callBackSuccessFn, clientName) {
  if (asUserId) {
    user.sub = asUserId
  }

  return dispatch => {
    dispatch({ type: CONTINUE_ENCOUNTER_CHECK })
    return http
      .getRequest(
        `/patients/${patientId}/unfinished-encounter?author_id=${user.sub}`
      )
      .then(res => {
          if (res?.currentUserEncounter) {
          callBackSuccessFn()
        } else {
          dispatch(encounterSearch(patientId, user, clientName))
        }
      })
  }
}

export function encounterStart (patientId, user, type, meta, redirectTo, navigate, callback, client) {
  const userId = user.sub

  return (dispatch, getState) => {
    const encounter = {
      author_id: userId,
      subject_id: patientId,
      type,
      user,
      meta
    }

    const clientName = client ?? getState().patient.clientName

    if (!(patientId && encounter)) {
      return null
    }

    dispatch({
      type: ENCOUNTER_START,
      encounterType: type,
      encounter
    })
    return http
      .postRequest(`/patients/${patientId}/encounters`, encounter, { clientName })
      .then(response => {
        let url = `/patient/${clientName}/${patientId}/assessment/${redirectTo}`

        dispatch({
          type: ENCOUNTER_STARTED,
          encounter: response
        })
        dispatch(notify('Encounter started'))
        navigate?.(url)
        callback?.()
      })
      .catch(err => {
        if (err.response && err.response.status === 403) {
          dispatch({
            type: ENCOUNTER_START_ERROR,
            error: err.response.data.message
          })
          dispatch(notify(err.response.data.message))
          dispatch(encounterSearch(patientId, user, clientName))
        } else {
          dispatch(notify('There was an error starting your encounter'))
          throw err
        }
      })
  }
}

export function encounterSign (encounter, formData, navigate, signingUser) {
  const originalType = formData.type
  formData.type = formData.reportType ?? formData.type
  const patientId = encounter.subject_id
  let validation = encounterBodySchema.safeParse(formData)
  console.log(validation)
  if (signingUser) {
    validation.data.signingUser = signingUser
  }
  if (validation.data) {
    validation.data.type = originalType
  }
  return (dispatch, getState) => {
    if (!validation.success) {
      return dispatch(notify('There was an error signing your encounter.', true))
    }

    const {
      globalVars: {
        params: { clientName }
      }
    } = getState()

    dispatch({
      type: ENCOUNTER_SIGN,
      encounter
    })
    return http
      .postRequest(
        `/patients/${patientId}/encounters/${encounter.id}/finalize`,
        validation.data
      )
      .then(response => {
        let url = `/patient/${clientName}/${patientId}/patientFolder`

        dispatch({
          type: ENCOUNTER_SIGNED,
          encounter: response,
          redirectTo: url
        })
        dispatch(notify('Encounter was signed and finalized'))
        dispatch(compartmentReceive(patientId))
        dispatch(init(patientId))
        navigate(url)
      })
      .catch(err => {
        dispatch(notify('There was an error signing your encounter.', true))
        dispatch({
          type: ENCOUNTER_SIGN_ERROR,
          error: err
        })
      })
  }
}

export function encounterGetChanges (patientId, encounterId, type) {
  return dispatch => {
    dispatch({ type: ENCOUNTER_GET_CHANGES })
    return http
      .getRequest(
        `/patients/${patientId}/encounters/${encounterId}/changes?type=${type}`
      )
      .then(response => {
        dispatch({
          type: ENCOUNTER_GET_CHANGES_SUCCESS,
          reportData: response.data.data,
          reportHtml: response.data.html
        })
      })
  }
}

export function encounterGet (patientId, encounterId) {
  return dispatch => {
    dispatch({
      type: ENCOUNTER_GET,
      patientId,
      encounterId
    })
    return http
      .getRequest(`/patients/${patientId}/encounters/${encounterId}`)
      .then(response => {
        dispatch({
          type: ENCOUNTER_GOT,
          encounter: response
        })
      })
  }
}

export function encounterReset () {
  return dispatch => {
    dispatch({ type: ENCOUNTER_RESET })
  }
}

export function pause () {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.encounter &&
      state.encounter.encounter &&
      !state.encounter.signed
    ) {
      dispatch({ type: ENCOUNTER_DURATION })
      http
        .postRequest(
          `/patients/${state.encounter.encounter.subject_id}/encounters/${state.encounter.encounter.document.context_id}/pause`,
          {}
        )
        .then(response => {
          dispatch({
            type: ENCOUNTER_GOT,
            encounter: response
          })
          dispatch(notify('Encounter paused'))
        })
        .catch(err => {
          dispatch({
            type: ENCOUNTER_DURATION_ERROR,
            error: err
          })
          dispatch(
            notify(
              `Encounter pause error: ${
                  err?.response?.data?.description ||
                  err?.response?.data?.message ||
                err
              }`
            ),
            true
          )
        })
    }
  }
}

export function resume () {
  return (dispatch, getState) => {
    const state = getState()

    if (state.encounter && state.encounter.encounter) {
      dispatch({ type: ENCOUNTER_DURATION })
      http
        .postRequest(
          `/patients/${state.encounter.encounter.subject_id}/encounters/${state.encounter.encounter.document.context_id}/resume`,
          {}
        )
        .then(response => {
          dispatch({
            type: ENCOUNTER_GOT,
            encounter: response
          })
          dispatch(notify('Encounter resumed'))
        })
        .catch(err => {
          dispatch({
            type: ENCOUNTER_DURATION_ERROR,
            error: err
          })
          dispatch(
            notify(
              `Encounter resume error: ${
                  err?.response?.data?.description ||
                  err?.response?.data?.message ||
                err
              }`,
              true
            )
          )
        })
    }
  }
}
